// @ts-nocheck
import useIsScrollTop from "@store/Framework/Common/Hooks/useIsScrollTop";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { useEagerLogoLoading } from "@insite/client-framework/Common/EagerLoadingLogo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { extractStylesToArray, isSafe } from "@insite/client-framework/Common/Utilities/isSafeStyles";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import Hidden from "@insite/mobius/Hidden";
import Img, { ImgProps } from "@insite/mobius/Img";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import parse from "html-react-parser";
import * as React from "react";
import { useSelector } from "react-redux";
import { css } from "styled-components";

const enum fields {
    logoImage = "logoImage",
    maxWidth = "maxWidth",
    maxHeight = "maxHeight",
    fixedLogoImage = "fixedLogoImage",
    alignment = "alignment",
    fixedAlignment = "fixedAlignment",
    hasFixedHeaderContentText = "hasFixedHeaderContentText",
    fixedHeaderContent = "fixedHeaderContent",
    hasFixedHeaderLogo = "hasFixedHeaderLogo",
    isFixedHeaderLogoSizeSpecific = "isFixedHeaderLogoSizeSpecific",
    fixedLogoMaxWidth = "fixedLogoMaxWidth",
    fixedLogoMaxHeight = "fixedLogoMaxHeight",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.logoImage]: string;
        [fields.maxWidth]: number;
        [fields.maxHeight]: number;
        [fields.fixedLogoImage]: string;
        [fields.fixedAlignment]: string;
        [fields.alignment]: string;
        [fields.hasFixedHeaderContentText]: boolean;
        [fields.fixedHeaderContent]: string;
        [fields.hasFixedHeaderLogo]: boolean;
        [fields.isFixedHeaderLogoSizeSpecific]: boolean;
        [fields.fixedLogoMaxWidth]: number;
        [fields.fixedLogoMaxHeight]: number;
    };
    extendedStyles?: LogoStyles;
}

type Props = OwnProps;
export interface LogoStyles {
    wrapper?: InjectableCss;
    link?: LinkPresentationProps;
    img?: ImgProps;
}

export const logoStyles: LogoStyles = {
    wrapper: {
        css: css`
            line-height: 0;

            img {
                width: auto;
            }
        `,
    },
    link: {
        css: css`
            transition: opacity 0.3s ease-in-out;

            &:hover,
            :active {
                text-decoration: none;
            }
        `,
    },
};

export const HeaderLogo: React.FunctionComponent<Props> = ({ fields, extendedStyles }: Props) => {
    const styles = useMergeStyles("logo", logoStyles, extendedStyles);
    const isEager = useEagerLogoLoading();
    const homePageLink = useSelector((state: ApplicationState) => getHomePageUrl(state));
    const isScrollTop = useIsScrollTop();

    if (!fields.logoImage && !fields.fixedLogoImage) {
        return null;
    }

    const minHeight = fields.isFixedHeaderLogoSizeSpecific
        ? Math.max(fields.fixedLogoMaxHeight, fields.maxHeight)
        : fields.maxHeight;

    const wrapperStyles: InjectableCss = {
        css: css`
            ${styles.wrapper.css}
            min-height: ${pxToRem(minHeight)};
            display: flex;
            align-items: center;
            justify-content: ${fields.alignment};
            min-width: 100%;

            ${isScrollTop &&
            css`
                justify-content: ${fields.fixedAlignment};
            `}
        `,
    };

    const logoImageStyles: ImgProps = {
        ...styles.img,
        css: css`
            ${styles.img}
            max-height: ${pxToRem(fields.maxHeight)};
            max-width: ${pxToRem(fields.maxWidth)};
            display: ${isScrollTop ? "none" : "block"};
        `,
    };

    const fixedHeaderLogoImageStyles: ImgProps = {
        ...styles.img,
        css: css`
            ${styles.img}
            max-height: ${pxToRem(fields.isFixedHeaderLogoSizeSpecific ? fields.fixedLogoMaxHeight : fields.maxHeight)};
            max-width: ${pxToRem(fields.isFixedHeaderLogoSizeSpecific ? fields.fixedLogoMaxWidth : fields.maxWidth)};
            display: ${isScrollTop ? "block" : "none"};
        `,
    };

    const mobileLogoImageStyles: ImgProps = {
        ...logoImageStyles,
        css: css`
            ${logoImageStyles?.css}
            display: block;
        `,
    };

    return (
        <StyledWrapper {...wrapperStyles}>
            <Link {...styles.link} href={homePageLink}>
                <Hidden below="sm">
                    {(fields.hasFixedHeaderLogo && fields.fixedLogoImage) || fields.hasFixedHeaderContentText ? (
                        <>
                            {fields.hasFixedHeaderLogo && fields.fixedLogoImage ? (
                                <Img
                                    loading={isEager ? "eager" : "lazy"}
                                    src={fields.fixedLogoImage}
                                    altText={translate("Home")}
                                    {...fixedHeaderLogoImageStyles}
                                />
                            ) : (
                                <StyledWrapper
                                    css={css`
                                        display: ${isScrollTop ? "block" : "none"};
                                    `}
                                >
                                    {parse(fields.fixedHeaderContent, parserOptions)}
                                </StyledWrapper>
                            )}
                            <Img
                                loading={isEager ? "eager" : "lazy"}
                                src={fields.logoImage}
                                altText={translate("Home")}
                                {...logoImageStyles}
                            />
                        </>
                    ) : (
                        <Img
                            loading={isEager ? "eager" : "lazy"}
                            src={fields.logoImage}
                            altText={translate("Home")}
                            {...logoImageStyles}
                        />
                    )}
                </Hidden>
                <Hidden above="xs">
                    <Img
                        loading={isEager ? "eager" : "lazy"}
                        src={fields.logoImage}
                        altText={translate("Home")}
                        {...mobileLogoImageStyles}
                    />
                </Hidden>
            </Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: HeaderLogo,
    definition: {
        group: "Header",
        displayName: "Header Logo",
        icon: "Logo",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.logoImage,
                displayName: "Logo Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
            },
            {
                name: fields.maxWidth,
                displayName: "Logo max width",
                editorTemplate: "IntegerField",
                defaultValue: 70,
                fieldType: "Translatable",
            },
            {
                name: fields.maxHeight,
                displayName: "Logo max height",
                editorTemplate: "IntegerField",
                defaultValue: 70,
                fieldType: "Translatable",
            },
            {
                name: fields.alignment,
                displayName: "Header Content Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "center",
                fieldType: "General",
            },
            {
                name: fields.fixedAlignment,
                displayName: "Fixed Header Content Alignment",
                editorTemplate: "DropDownField",
                options: [
                    { displayName: "Left", value: "flex-start" },
                    { displayName: "Center", value: "center" },
                    { displayName: "Right", value: "flex-end" },
                ],
                defaultValue: "center",
                fieldType: "General",
            },
            {
                name: fields.hasFixedHeaderContentText,
                displayName: "Fixed Header Specific Text",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: true,
                variant: "toggle",
                isVisible: widget => !widget.fields.hasFixedHeaderLogo,
            },
            {
                name: fields.fixedHeaderContent,
                displayName: "Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                validate: (html: string) => {
                    const arrayOfStyles = extractStylesToArray(html);
                    return arrayOfStyles?.every(style => isSafe(style))
                        ? null
                        : "The HTML contains invalid styles that prevent it from being saved";
                },
                isVisible: widget => widget.fields.hasFixedHeaderContentText && !widget.fields.hasFixedHeaderLogo,
            },
            {
                name: fields.hasFixedHeaderLogo,
                displayName: "Fixed Header Specific Logo",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: true,
                variant: "toggle",
                isVisible: widget => !widget.fields.hasFixedHeaderContentText,
            },
            {
                name: fields.fixedLogoImage,
                displayName: "Fixed Header Logo Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.hasFixedHeaderLogo,
            },
            {
                name: fields.isFixedHeaderLogoSizeSpecific,
                displayName: "Change Fixed Header Logo Size",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: true,
                variant: "toggle",
                isVisible: widget => widget.fields.hasFixedHeaderLogo && !widget.fields.hasFixedHeaderContentText,
            },
            {
                name: fields.fixedLogoMaxWidth,
                displayName: "Fixed Header Logo max width",
                editorTemplate: "IntegerField",
                defaultValue: 70,
                fieldType: "Translatable",
                isVisible: widget => widget.fields.isFixedHeaderLogoSizeSpecific,
            },
            {
                name: fields.fixedLogoMaxHeight,
                displayName: "Fixed Header Logo max height",
                editorTemplate: "IntegerField",
                defaultValue: 70,
                fieldType: "Translatable",
                isVisible: widget => widget.fields.isFixedHeaderLogoSizeSpecific,
            },
        ],
    },
};

export default widgetModule;
